import { c as _c } from "react/compiler-runtime";
import yakRouterAbi from "@/constants/abi/yakRouterAbi";
import { chainSettings, type ChainType, fallbackChainId, fallbackMulticall } from "@/constants/chain-specific";
import { useEffect, useState, useMemo } from "react";
import type { Address } from "viem";
import { useReadContracts } from "wagmi";
import { erc20Abi, formatUnits } from "viem";
import { defaultNativeAddress } from '../../constants/chain-specific/chains';
type BestPathOutputType = {
  amountOut: number;
  gas: number;
  path: Address[];
};
type BestPathReturnType = {
  quote?: BestPathOutputType;
};
interface SwapBestPathProps {
  amountIn: number | bigint;
  tokenIn: Address | string;
  tokenOut: Address | string;
  maxSteps?: number;
  gasPrice?: number | bigint;
  chainId?: ChainType;
}
export interface AggregatorQuote {
  router: Address;
  amounts: readonly bigint[] | null;
  amountOutBigInt: bigint;
  amountOut: string;
  path: readonly Address[] | null;
  gas: number | null;
  adapters: readonly Address[] | null;
  cantFind: boolean;
  error: boolean;
}
const FEE_CACHE: { [address in string]?: {
  sellTax?: number;
  buyTax?: number;
} } = {};
export function useSwapBestPath(t0) {
  const $ = _c(61);
  const {
    amountIn,
    tokenIn,
    tokenOut,
    maxSteps: t1,
    gasPrice: t2,
    chainId: t3
  } = t0;
  const maxSteps = t1 === undefined ? 3 : t1;
  const gasPrice = t2 === undefined ? 3500000000 : t2;
  const chainId = t3 === undefined ? fallbackChainId : t3;
  const [amounts, setAmounts] = useState(null);
  const [path, setPath] = useState(null);
  const [gas, setGas] = useState(0);
  const [adapters, setAdapters] = useState(null);
  const [isCantFInd, setIsCantFind] = useState(false);
  const t4 = (chainSettings[chainId] as any).yakRouter as Address;
  let t5;
  if ($[0] !== chainId || $[1] !== t4) {
    t5 = {
      address: t4,
      abi: yakRouterAbi,
      chainId
    };
    $[0] = chainId;
    $[1] = t4;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  const yakRouter = t5 as const;
  const t6 = amountIn ?? 0;
  let t7;
  if ($[3] !== t6) {
    t7 = BigInt(t6);
    $[3] = t6;
    $[4] = t7;
  } else {
    t7 = $[4];
  }
  const t8 = tokenIn === defaultNativeAddress ? chainSettings[chainId].wNative.address as Address : tokenIn as Address;
  const t9 = tokenOut === defaultNativeAddress ? chainSettings[chainId].wNative.address as Address : tokenOut as Address;
  let t10;
  if ($[5] !== maxSteps) {
    t10 = BigInt(maxSteps);
    $[5] = maxSteps;
    $[6] = t10;
  } else {
    t10 = $[6];
  }
  let t11;
  if ($[7] !== gasPrice) {
    t11 = BigInt(gasPrice);
    $[7] = gasPrice;
    $[8] = t11;
  } else {
    t11 = $[8];
  }
  let t12;
  if ($[9] !== t10 || $[10] !== t11 || $[11] !== t7 || $[12] !== t8 || $[13] !== t9) {
    t12 = [t7, t8, t9, t10, t11];
    $[9] = t10;
    $[10] = t11;
    $[11] = t7;
    $[12] = t8;
    $[13] = t9;
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  let t13;
  if ($[15] !== t12 || $[16] !== yakRouter) {
    t13 = {
      ...yakRouter,
      functionName: "findBestPathWithGas",
      args: t12
    };
    $[15] = t12;
    $[16] = yakRouter;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  const t14 = tokenIn === defaultNativeAddress ? chainSettings[chainId].wNative.address as Address : tokenIn as Address;
  let t15;
  if ($[18] !== chainId || $[19] !== t14) {
    t15 = {
      address: t14,
      chainId,
      abi: erc20Abi,
      functionName: "decimals"
    };
    $[18] = chainId;
    $[19] = t14;
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  const t16 = tokenOut === defaultNativeAddress ? chainSettings[chainId].wNative.address as Address : tokenOut as Address;
  let t17;
  if ($[21] !== chainId || $[22] !== t16) {
    t17 = {
      address: t16,
      chainId,
      abi: erc20Abi,
      functionName: "decimals"
    };
    $[21] = chainId;
    $[22] = t16;
    $[23] = t17;
  } else {
    t17 = $[23];
  }
  let t18;
  if ($[24] !== t13 || $[25] !== t15 || $[26] !== t17) {
    t18 = [t13, t15, t17];
    $[24] = t13;
    $[25] = t15;
    $[26] = t17;
    $[27] = t18;
  } else {
    t18 = $[27];
  }
  const contracts = t18 as const;
  const t19 = (chainSettings[chainId] as any).multicall ?? fallbackMulticall;
  let t20;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = {
      refetchInterval: 10000,
      refetchOnWindowFocus: false
    };
    $[28] = t20;
  } else {
    t20 = $[28];
  }
  let t21;
  if ($[29] !== contracts || $[30] !== t19) {
    t21 = {
      contracts,
      allowFailure: false,
      multicallAddress: t19,
      query: t20
    };
    $[29] = contracts;
    $[30] = t19;
    $[31] = t21;
  } else {
    t21 = $[31];
  }
  const {
    data,
    isError
  } = useReadContracts(t21);
  let t22;
  bb0: {
    if (isError) {
      let t23;
      if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
        t23 = [];
        $[32] = t23;
      } else {
        t23 = $[32];
      }
      let t24;
      if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
        t24 = {
          amounts: t23,
          gas: 0,
          path: [],
          adapters: [],
          isCantFind: true
        };
        $[33] = t24;
      } else {
        t24 = $[33];
      }
      t22 = t24;
      break bb0;
    }
    if (data?.[0]) {
      const t23 = data[0].amounts;
      const t24 = Number(data[0].gasEstimate);
      let t25;
      if ($[34] !== data[0].adapters || $[35] !== data[0].amounts || $[36] !== data[0].path || $[37] !== t24) {
        t25 = {
          amounts: t23,
          gas: t24,
          path: data[0].path,
          adapters: data[0].adapters,
          isCantFind: false
        };
        $[34] = data[0].adapters;
        $[35] = data[0].amounts;
        $[36] = data[0].path;
        $[37] = t24;
        $[38] = t25;
      } else {
        t25 = $[38];
      }
      t22 = t25;
      break bb0;
    }
    t22 = null;
  }
  const pathCalculations = t22;
  let t23;
  let t24;
  if ($[39] !== pathCalculations) {
    t23 = () => {
      if (!pathCalculations) {
        return;
      }
      const {
        amounts: newAmounts,
        gas: newGas,
        path: newPath,
        adapters: newAdapters,
        isCantFind: newIsCantFind
      } = pathCalculations;
      setAmounts(newAmounts);
      setGas(newGas);
      setPath(newPath);
      setAdapters(newAdapters);
      setIsCantFind(newIsCantFind);
    };
    t24 = [pathCalculations];
    $[39] = pathCalculations;
    $[40] = t23;
    $[41] = t24;
  } else {
    t23 = $[40];
    t24 = $[41];
  }
  useEffect(t23, t24);
  const t25 = (chainSettings[chainId] as any).yakRouter as Address;
  let t26;
  if ($[42] !== data?.[0].amounts) {
    t26 = data?.[0].amounts[data?.[0].amounts?.length - 1] ?? BigInt(0);
    $[42] = data?.[0].amounts;
    $[43] = t26;
  } else {
    t26 = $[43];
  }
  let t27;
  if ($[44] !== data?.[0].amounts) {
    t27 = data?.[0].amounts[data?.[0].amounts?.length - 1] ?? BigInt(0);
    $[44] = data?.[0].amounts;
    $[45] = t27;
  } else {
    t27 = $[45];
  }
  const t28 = Number(data?.[2]);
  let t29;
  if ($[46] !== t27 || $[47] !== t28) {
    t29 = formatUnits(t27, t28);
    $[46] = t27;
    $[47] = t28;
    $[48] = t29;
  } else {
    t29 = $[48];
  }
  let t30;
  if ($[49] !== adapters || $[50] !== amounts || $[51] !== gas || $[52] !== isCantFInd || $[53] !== isError || $[54] !== path || $[55] !== t25 || $[56] !== t26 || $[57] !== t29) {
    t30 = {
      router: t25,
      amounts,
      amountOutBigInt: t26,
      amountOut: t29,
      path,
      gas,
      adapters,
      cantFind: isCantFInd,
      error: isError
    };
    $[49] = adapters;
    $[50] = amounts;
    $[51] = gas;
    $[52] = isCantFInd;
    $[53] = isError;
    $[54] = path;
    $[55] = t25;
    $[56] = t26;
    $[57] = t29;
    $[58] = t30;
  } else {
    t30 = $[58];
  }
  const t31 = t30 as AggregatorQuote;
  let t32;
  if ($[59] !== t31) {
    t32 = {
      quote: t31
    };
    $[59] = t31;
    $[60] = t32;
  } else {
    t32 = $[60];
  }
  return t32;
}